"use client";

// Input form for the Ashtakvarga calculator widget.

import dynamic from "next/dynamic";
import type { PlaceResult } from "@/lib/api/misc";
import { LOADING_STEPS } from "./AshtakvargaTypes";

const PlaceAutocomplete = dynamic(() => import("@/components/PlaceAutocomplete"), { ssr: false });

interface Props {
  name: string;
  setName: (v: string) => void;
  birthDate: string;
  setBirthDate: (v: string) => void;
  birthTime: string;
  setBirthTime: (v: string) => void;
  birthPlace: string;
  setBirthPlace: (v: string) => void;
  setSelectedPlace: (p: PlaceResult) => void;
  loading: boolean;
  loadingStep: number;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
}

export function AshtakvargaForm({
  name,
  setName,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  loading,
  loadingStep,
  error,
  onSubmit,
}: Props) {
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <label
            className="block text-[12px] font-semibold mb-1"
            style={{ color: "var(--ish-dark)" }}
          >
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            maxLength={200}
            className="input-sacred w-full"
          />
        </div>

        {/* Birth date */}
        <div>
          <label
            className="block text-[12px] font-semibold mb-1"
            style={{ color: "var(--ish-dark)" }}
          >
            Date of Birth <span style={{ color: "var(--ish-accent)" }}>*</span>
          </label>
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            max={todayIso}
            required
            className="input-sacred w-full"
          />
        </div>

        {/* Birth time */}
        <div>
          <label
            className="block text-[12px] font-semibold mb-1"
            style={{ color: "var(--ish-dark)" }}
          >
            Time of Birth{" "}
            <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
              (recommended for accurate lagna)
            </span>
          </label>
          <input
            type="time"
            value={birthTime}
            onChange={(e) => setBirthTime(e.target.value)}
            className="input-sacred w-full"
          />
        </div>

        {/* Birth place */}
        <div>
          <label
            className="block text-[12px] font-semibold mb-1"
            style={{ color: "var(--ish-dark)" }}
          >
            Birth Place <span style={{ color: "var(--ish-accent)" }}>*</span>
          </label>
          <PlaceAutocomplete
            value={birthPlace}
            onChange={setBirthPlace}
            onSelect={(place) => {
              setSelectedPlace(place);
              setBirthPlace(place.name);
            }}
            placeholder="City, country..."
            className="input-sacred w-full"
          />
        </div>
      </div>

      {error && (
        <p className="text-[13px]" style={{ color: "#c0392b" }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={loading || !birthDate}
        className="btn-sacred w-full py-3 text-[14px] font-semibold rounded-lg disabled:opacity-50"
      >
        {loading ? LOADING_STEPS[loadingStep] : "Calculate Ashtakvarga →"}
      </button>
    </form>
  );
}
